import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Check, Lock, Search, Facebook, BadgeCheck, Rocket, Plug } from 'lucide-react';
import api from '../utils/api';
import { DISCLAIMER_LINES, PLAYBOOKS } from './guidedData';
import StepWizard from './StepWizard';
import ActivateWizard from './ActivateWizard';
import Scoreboard from './Scoreboard';

const PB_ICONS = { search: Search, facebook: Facebook, badge: BadgeCheck, rocket: Rocket };

const DisclaimerGate = ({ onAgreed }) => {
  const [checked, setChecked] = useState(false);
  const [saving, setSaving] = useState(false);
  const agree = async () => {
    setSaving(true);
    try { await api.post('/guided/agree'); onAgreed(); } finally { setSaving(false); }
  };
  return (
    <div className="max-w-md" data-testid="disclaimer-gate">
      <h2 className="text-2xl font-bold text-white mb-3">Let's get you set up.</h2>
      <p className="text-light-gray mb-6">
        You'll (1) connect your ad accounts, (2) launch campaigns, (3) watch the leads come in.
      </p>
      <div className="rounded-2xl border border-divider bg-card-bg p-5 mb-5">
        <p className="text-white font-bold mb-3">You're in the driver's seat. GritClicks helps you build and manage ads inside YOUR OWN Google and Meta accounts.</p>
        <ul className="space-y-2 mb-4">
          {DISCLAIMER_LINES.map((l, i) => (
            <li key={i} className="text-sm text-light-gray flex gap-2"><span className="text-lime shrink-0">•</span>{l}</li>
          ))}
        </ul>
        <label className="flex items-center gap-3 p-3 rounded-xl bg-black border border-divider cursor-pointer hover:border-lime/40 transition-colors">
          <input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)}
            className="w-5 h-5 accent-[#c6f24e]" data-testid="disclaimer-checkbox" />
          <span className="text-white font-semibold">I understand and agree.</span>
        </label>
      </div>
      <button onClick={agree} disabled={!checked || saving} data-testid="disclaimer-continue-btn"
        className="w-full py-4 rounded-xl bg-lime text-black font-black text-lg disabled:opacity-30 disabled:cursor-not-allowed hover:bg-lime/90 transition-colors">
        {saving ? 'Saving…' : "Let's go →"}
      </button>
    </div>
  );
};

export default function SetupJourney() {
  const [progress, setProgress] = useState(null);
  const [connections, setConnections] = useState(null);
  const [active, setActive] = useState(null);

  const load = useCallback(async () => {
    const [p, c] = await Promise.all([api.get('/guided/progress'), api.get('/guided/connections/status')]);
    setProgress(p.data);
    setConnections(c.data);
  }, []);

  useEffect(() => { load().catch(() => {}); }, [load]);

  if (!progress || !connections) {
    return <div className="text-gray py-8" data-testid="journey-loading">Loading your setup…</div>;
  }

  const pbState = (id) => progress.playbooks?.[id] || null;
  const googleConnected = !!connections.google?.connected;
  const metaConnected = !!connections.meta?.connected;
  const anyConnected = googleConnected || metaConnected;

  // Connection steps only count as done when the OAuth connection is REAL
  const isDone = (id) => {
    if (id === 'google') return googleConnected;
    if (id === 'meta') return metaConnected;
    return !!pbState(id)?.completed;
  };
  // Steps after the connection steps require at least one REAL ad connection
  const isUnlocked = (idx) => {
    const id = PLAYBOOKS[idx].id;
    if (idx === 0) return true;
    if (id === 'meta') return googleConnected;
    if (id === 'lsa') return anyConnected;
    if (id === 'activate') return anyConnected && isDone('lsa');
    return isDone(PLAYBOOKS[idx - 1].id);
  };

  const finishActive = () => { setActive(null); load().catch(() => {}); };

  return (
    <div data-testid="setup-journey">
      {!progress.agreed ? (
        <DisclaimerGate onAgreed={() => setProgress({ ...progress, agreed: true })} />
      ) : active ? (
        active === 'activate'
          ? <ActivateWizard saved={pbState('activate')} connections={connections} onFinish={finishActive} onExit={finishActive} />
          : <StepWizard playbookId={active} saved={pbState(active)} connections={connections} onFinish={finishActive} onExit={finishActive} refreshConnections={load} />
      ) : (
        <>
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-1">Your Setup Journey</h2>
          <p className="text-light-gray mb-5">One step at a time. Each stage unlocks the next.</p>

          {!anyConnected && (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[#ec6a1c]/60 bg-[#ec6a1c]/10 p-4 mb-5" data-testid="connect-required-banner">
              <p className="text-sm font-bold text-[#14160f] flex items-center gap-2"><Plug size={16} className="text-[#c2530e]" /> Connect your ad account to continue — later steps and campaign launch stay locked until it's really connected.</p>
              <Link to="/connections" data-testid="connect-required-btn"
                className="px-4 py-2 rounded-lg bg-[#ec6a1c] text-white font-bold text-sm hover:opacity-90 shrink-0">Go to Connections →</Link>
            </div>
          )}

          <div className="space-y-3 mb-10">
            {PLAYBOOKS.map((pb, idx) => {
              const done = isDone(pb.id);
              const unlocked = isUnlocked(idx);
              const started = !!pbState(pb.id);
              const Icon = PB_ICONS[pb.icon] || Search;
              return (
                <button key={pb.id} disabled={!unlocked} onClick={() => setActive(pb.id)} data-testid={`playbook-card-${pb.id}`}
                  className={`w-full rounded-2xl border p-5 flex items-center gap-4 text-left transition-colors ${
                    done ? 'border-lime/60 bg-lime/5' : unlocked ? 'border-divider bg-card-bg hover:border-lime/50' : 'border-divider bg-black opacity-60 cursor-not-allowed'}`}>
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${done ? 'bg-lime text-black' : 'bg-lime/10 border border-lime/30 text-lime'}`}>
                    {done ? <Check size={22} /> : unlocked ? <Icon size={22} /> : <Lock size={20} className="text-gray" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs uppercase tracking-wide text-gray font-bold">Playbook {pb.num}</p>
                    <p className="text-white font-bold text-lg">{pb.title}</p>
                    <p className="text-sm text-light-gray">{pb.desc}</p>
                  </div>
                  <span className={`text-sm font-bold shrink-0 ${done ? 'text-lime' : unlocked ? 'text-lime' : 'text-gray'}`} data-testid={`playbook-status-${pb.id}`}>
                    {done ? 'Done ✓' : unlocked ? (started ? 'Resume →' : 'Start →') : 'Locked'}
                  </span>
                </button>
              );
            })}
          </div>

          <Scoreboard />
        </>
      )}
    </div>
  );
}
